import {
  ExploredScriptureSchema,
  ReadingProgressSchema,
  StudyBookmarkSchema,
  StudyDataSchema,
  StudyHighlightSchema,
  StudyNoteSchema,
  TalkStudyStateSchema,
  ThreadProgressSchema,
  type HighlightColor,
  type ExploredScripture,
  type ReadingProgress,
  type StudyBookmark,
  type StudyData,
  type StudyHighlight,
  type StudyNote,
  type TalkStudyState,
  type ThreadProgress,
} from '../data/schema';

export interface HighlightCommand {
  talkId: string;
  paragraphId: string;
  startOffset: number;
  endOffset: number;
  color: HighlightColor;
  textSnippet: string;
}

export interface ProgressCommand {
  talkId: string;
  paragraphId: string | null;
  percent: number;
  completed: boolean;
}

export interface NoteCommand {
  talkId: string;
  paragraphId: string;
  noteText: string;
}

export interface StudyStore {
  loadStudy(conferenceId: string): Promise<StudyData>;
  loadProgress(): Promise<ReadingProgress[]>;
  loadBookmarks(): Promise<StudyBookmark[]>;
  loadThreadProgress(conferenceId: string): Promise<ThreadProgress[]>;
  loadTalk(talkId: string): Promise<TalkStudyState>;
  saveProgress(command: ProgressCommand): Promise<ReadingProgress>;
  addHighlight(command: HighlightCommand): Promise<StudyHighlight>;
  removeHighlight(highlightId: string | number): Promise<void>;
  saveNote(command: NoteCommand): Promise<StudyNote>;
  removeNote(talkId: string, paragraphId: string): Promise<void>;
  saveThreadProgress(
    conferenceId: string,
    threadId: string,
    completedTalkIds: string[],
    completed: boolean,
  ): Promise<ThreadProgress>;
  saveBookmark(talkId: string): Promise<StudyBookmark>;
  removeBookmark(talkId: string): Promise<void>;
  saveExploredScripture(
    bookId: string,
    chapter: number,
    verses: number[],
  ): Promise<ExploredScripture>;
}

export class StudyStoreError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'StudyStoreError';
  }
}

type Fetcher = typeof fetch;

async function apiRequest(
  fetcher: Fetcher,
  url: string,
  init?: RequestInit,
): Promise<unknown> {
  const headers = new Headers(init?.headers);
  if (init?.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await fetcher(url, {
    credentials: 'same-origin',
    ...init,
    headers,
  });
  if (!response.ok) {
    let message = `Study request failed with status ${String(response.status)}.`;
    try {
      const body = (await response.json()) as { error?: unknown };
      if (typeof body.error === 'string') message = body.error;
    } catch {
      // The status remains the useful failure signal for a non-JSON response.
    }
    throw new StudyStoreError(message, response.status);
  }
  if (response.status === 204) return undefined;
  return response.json();
}

export function createStudyStore(fetcher: Fetcher = fetch): StudyStore {
  return {
    async loadStudy(conferenceId) {
      return StudyDataSchema.parse(
        await apiRequest(
          fetcher,
          `/api/v2/study?conferenceId=${encodeURIComponent(conferenceId)}`,
        ),
      );
    },

    async loadProgress() {
      const response = await apiRequest(fetcher, '/api/v2/study/progress');
      if (!Array.isArray(response)) {
        throw new StudyStoreError('Invalid reading progress response.', 500);
      }
      return response.map((item) => ReadingProgressSchema.parse(item));
    },

    async loadThreadProgress(conferenceId) {
      const response = await apiRequest(
        fetcher,
        `/api/v2/study/threads?conferenceId=${encodeURIComponent(conferenceId)}`,
      );
      if (!Array.isArray(response)) {
        throw new StudyStoreError('Invalid thread progress response.', 500);
      }
      return response.map((item) => ThreadProgressSchema.parse(item));
    },

    async loadBookmarks() {
      const response = await apiRequest(fetcher, '/api/v2/study/bookmarks');
      if (!Array.isArray(response)) {
        throw new StudyStoreError('Invalid bookmarks response.', 500);
      }
      return response.map((item) => StudyBookmarkSchema.parse(item));
    },

    async loadTalk(talkId) {
      const safeTalkId = encodeURIComponent(talkId);
      return TalkStudyStateSchema.parse(
        await apiRequest(fetcher, `/api/v2/study/talks/${safeTalkId}`),
      );
    },

    async saveProgress(command) {
      const safeTalkId = encodeURIComponent(command.talkId);
      return ReadingProgressSchema.parse(
        await apiRequest(
          fetcher,
          `/api/v2/study/talks/${safeTalkId}/progress`,
          {
            method: 'PUT',
            body: JSON.stringify({
              paragraphId: command.paragraphId,
              percent: command.percent,
              completed: command.completed,
            }),
          },
        ),
      );
    },

    async addHighlight(command) {
      const safeTalkId = encodeURIComponent(command.talkId);
      return StudyHighlightSchema.parse(
        await apiRequest(
          fetcher,
          `/api/v2/study/talks/${safeTalkId}/highlights`,
          {
            method: 'POST',
            body: JSON.stringify({
              paragraphId: command.paragraphId,
              startOffset: command.startOffset,
              endOffset: command.endOffset,
              color: command.color,
              textSnippet: command.textSnippet,
            }),
          },
        ),
      );
    },

    async removeHighlight(highlightId) {
      await apiRequest(
        fetcher,
        `/api/v2/study/highlights/${encodeURIComponent(String(highlightId))}`,
        { method: 'DELETE' },
      );
    },

    async saveNote(command) {
      const safeTalkId = encodeURIComponent(command.talkId);
      const safeParagraphId = encodeURIComponent(command.paragraphId);
      return StudyNoteSchema.parse(
        await apiRequest(
          fetcher,
          `/api/v2/study/talks/${safeTalkId}/notes/${safeParagraphId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ noteText: command.noteText }),
          },
        ),
      );
    },

    async removeNote(talkId, paragraphId) {
      await apiRequest(
        fetcher,
        `/api/v2/study/talks/${encodeURIComponent(talkId)}/notes/${encodeURIComponent(paragraphId)}`,
        { method: 'DELETE' },
      );
    },

    async saveThreadProgress(
      conferenceId,
      threadId,
      completedTalkIds,
      completed,
    ) {
      return ThreadProgressSchema.parse(
        await apiRequest(
          fetcher,
          `/api/v2/study/threads/${encodeURIComponent(threadId)}`,
          {
            method: 'PUT',
            body: JSON.stringify({
              conferenceId,
              completedTalkIds,
              completed,
            }),
          },
        ),
      );
    },

    async saveBookmark(talkId) {
      return StudyBookmarkSchema.parse(
        await apiRequest(
          fetcher,
          `/api/v2/study/bookmarks/${encodeURIComponent(talkId)}`,
          { method: 'PUT' },
        ),
      );
    },

    async removeBookmark(talkId) {
      await apiRequest(
        fetcher,
        `/api/v2/study/bookmarks/${encodeURIComponent(talkId)}`,
        { method: 'DELETE' },
      );
    },

    async saveExploredScripture(bookId, chapter, verses) {
      const selection = verses.join(',');
      return ExploredScriptureSchema.parse(
        await apiRequest(
          fetcher,
          `/api/v2/study/scriptures/${encodeURIComponent(bookId)}/${String(chapter)}/${encodeURIComponent(selection)}`,
          {
            method: 'PUT',
            body: JSON.stringify({ verses }),
          },
        ),
      );
    },
  };
}
